import React, { useState, useCallback, useRef } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button,
} from '../components/ui'
import { Input } from '../components/ui/input'

export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    message: '',
    type: 'confirm',
    confirmText: 'Aceptar',
    cancelText: 'Cancelar',
    destructive: false,
    placeholder: '',
  })

  const resolveRef = useRef(null)
  const inputRef = useRef(null)

  const openDialog = useCallback((opts) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve
      setDialogState({
        open: true,
        title: opts.title || '',
        message: opts.message || '',
        type: opts.type || 'confirm',
        confirmText: opts.confirmText || 'Aceptar',
        cancelText: opts.cancelText || 'Cancelar',
        destructive: opts.destructive || false,
        placeholder: opts.placeholder || '',
      })
    })
  }, [])

  const closeDialog = useCallback((result) => {
    setDialogState((prev) => ({ ...prev, open: false }))
    resolveRef.current?.(result)
    resolveRef.current = null
  }, [])

  const confirm = useCallback((title, message, opts = {}) => {
    return openDialog({ type: 'confirm', title, message, ...opts })
  }, [openDialog])

  const alert = useCallback((title, message, opts = {}) => {
    return openDialog({ type: 'alert', title, message, confirmText: 'Entendido', ...opts })
  }, [openDialog])

  const prompt = useCallback((title, message, opts = {}) => {
    return openDialog({ type: 'prompt', title, message, ...opts })
  }, [openDialog])

  const handleConfirm = useCallback(() => {
    if (dialogState.type === 'prompt') {
      closeDialog(inputRef.current?.value ?? '')
    } else {
      closeDialog(true)
    }
  }, [dialogState.type, closeDialog])

  const handleCancel = useCallback(() => {
    closeDialog(dialogState.type === 'prompt' ? null : false)
  }, [dialogState.type, closeDialog])

  // ConfirmDialog como componente estable — el input usa ref nativo, no state
  const ConfirmDialog = useCallback(() => (
    <Dialog open={dialogState.open} onOpenChange={(v) => { if (!v) handleCancel() }}>
      <DialogContent compact className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{dialogState.title}</DialogTitle>
        </DialogHeader>
        {dialogState.message && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">{dialogState.message}</p>
        )}
        {dialogState.type === 'prompt' && (
          <Input
            ref={inputRef}
            defaultValue=""
            placeholder={dialogState.placeholder}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm() }}
          />
        )}
        <DialogFooter>
          {dialogState.type !== 'alert' && (
            <Button variant="outline" onClick={handleCancel}>
              {dialogState.cancelText}
            </Button>
          )}
          <Button
            variant={dialogState.destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
          >
            {dialogState.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ), [dialogState, handleConfirm, handleCancel])

  return { confirm, alert, prompt, ConfirmDialog }
}
